import { serializeToCamelCase } from '@olo-web/utils';
import { Capacitor } from '@capacitor/core';
import { useCustomerState, useCustomerDispatch } from '@olo-web/client-state';
import axios from 'axios';
import { useQuery, UseQueryResult } from 'react-query';
import { configHeaders } from '@olo-web/domain/customer';
import { clearSession } from '@olo-web/domain/customer/functions/sessionPersistence';

export type TUseCustomer = ICustomer & ICustomerInfo & { phone: string };

export interface IApiError extends Error {
  status?: number;
}

export const getCustomerProfile = async ({
  customerId,
  sessionId,
  sessionSecret,
  loginTimestamp,
}): Promise<ICustomer> => {
  try {
    const headers = configHeaders({
      customerId,
      sessionId,
      sessionSecret,
      loginTimestamp,
    });

    const { data } = await axios.get(`/api/customers/${customerId}/profile`, { headers });
    return data;
  } catch (error) {
    const err = new Error(
      error?.response?.data?.message || error?.response?.data?.error || error
    ) as IApiError;
    err.status = error?.response?.status;
    throw err;
  }
};

export const useCustomer = (): UseQueryResult<TUseCustomer, IApiError> => {
  const { customerId, sessionId, sessionSecret, loginTimestamp } = useCustomerState();
  const customerDispatch = useCustomerDispatch();

  return useQuery<ICustomer, IApiError, TUseCustomer>(
    ['customer', customerId],
    () => getCustomerProfile({ customerId, sessionId, sessionSecret, loginTimestamp }),
    {
      staleTime: 1000000,
      enabled: !!customerId,
      select: serializeToCamelCase,
      retry: 2,
      onError: (error) => {
        // Native-only: if a rehydrated session was revoked server-side, clear it
        // so we don't keep retrying with a dead credential. Web is unchanged.
        const status = error?.status;
        if (Capacitor.isNativePlatform() && (status === 401 || status === 403)) {
          clearSession();
          customerDispatch({ type: 'CLEAR' });
        }
      },
    }
  );
};
